import {FormattedMessage} from '@pinecast/i18n';
import type {CSSProperties} from '@pinecast/styles';
import {REDUCED_MOTION, useColors, useCSS} from '@pinecast/styles';

import ScreenReaderOnly from './ScreenReaderOnly';

const animationName = {
  '0%': {transform: 'scale(0)'},
  '80%': {transform: 'scale(0)'},
  '100%': {transform: 'scale(0)'},
  '40%': {transform: 'scale(1)'},
};

const DELAYS = ['0s', '-0.66s', '-0.33s'];

const Spinner = ({
  decorative = false,
  style,
  type = 'default',
}: {
  /**
   * Hide the spinner from assistive technology, for a caller that already
   * says what is loading in text of its own (`LoadingState`). Otherwise the
   * spinner is a status that reads "Loading…".
   */
  decorative?: boolean;
  style?: CSSProperties;
  type?: 'default' | 'focus' | 'subtle' | 'white';
}) => {
  const css = useCSS();
  const designColors = useColors();
  const backgroundColor =
    type === 'default'
      ? designColors.contentText
      : type === 'focus'
        ? designColors.headingBlue
        : type === 'subtle'
          ? designColors.contentBackground
          : '#fff';
  return (
    <div
      aria-hidden={decorative || undefined}
      className={css({
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        ...style,
      })}
      role={decorative ? undefined : 'status'}
    >
      {DELAYS.map(animationDelay => (
        <i
          aria-hidden
          className={css({
            animationName,
            animationDuration: '1s',
            animationIterationCount: 'infinite',
            backgroundColor,
            borderRadius: '20px',
            height: '10px',
            margin: '0 2px',
            width: '10px',

            animationDelay,

            // Without the animation the dots sit still at full size, which
            // reads as an ellipsis rather than as nothing at all.
            [REDUCED_MOTION]: {animationName: 'none'},
          })}
          key={animationDelay}
        />
      ))}
      {!decorative && (
        <ScreenReaderOnly>
          <FormattedMessage
            description="Loading state"
            defaultMessage="Loading…"
          />
        </ScreenReaderOnly>
      )}
    </div>
  );
};

export default Spinner;
